<template>
    <div ref="host" class="echart_host">
        <v-chart
            v-if="canRender"
            class="echart"
            :option="option"
            :init-options="initOptions"
            :autoresize="autoresize"
            :loading="loading"
            :theme="theme"
            @click="onChartClick"
            @mouseover="onChartMouseover"
            @mouseout="onChartMouseout"
            @finished="onChartFinished" />
    </div>
</template>

<script>
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart, LineChart, PieChart } from "echarts/charts";
import {
    GridComponent,
    LegendComponent,
    TitleComponent,
    TooltipComponent,
} from "echarts/components";
import VChart from "vue-echarts";

// Single project entry point for ECharts. Tree-shake by adding ONLY the
// chart types and components used in the app. When you introduce a new
// chart type (heatmap, scatter, etc.) extend this list — do not call
// `use()` from individual components.
use([
    CanvasRenderer,
    BarChart,
    LineChart,
    PieChart,
    GridComponent,
    LegendComponent,
    TitleComponent,
    TooltipComponent,
]);

export default {
    name: "EChart",
    components: { VChart },
    props: {
        option: { type: Object, required: true },
        // opts del tercer arg de `echarts.init`. Útil para pasar width/height
        // explícitos cuando el chart se monta en un contenedor que puede estar
        // en 0px al init (p.ej. dentro de un drawer mientras anima su entrada):
        // sin esto ECharts emite "Can't get DOM width or height". `autoresize`
        // reconcilia el tamaño real después.
        initOptions: { type: Object, default: null },
        autoresize: { type: Boolean, default: true },
        loading: { type: Boolean, default: false },
        theme: { type: String, default: undefined },
    },
    data() {
        return {
            // Difiere la creación del chart hasta que el host tenga tamaño. Si
            // ECharts inicializa en un contenedor de 0px (drawer animando su
            // entrada, CollapsibleSection colapsada, tab oculto) emite
            // "Can't get DOM width or height". Una vez con tamaño, `autoresize`
            // sigue reconciliando los cambios.
            canRender: false,
        };
    },
    mounted() {
        this.renderWhenSized();
    },
    beforeDestroy() {
        this.stopSizeObserver();
    },
    methods: {
        // Render inmediato si el host ya mide (caso común, chart visible). Si
        // no, esperamos con un ResizeObserver a que mida > 0 (se muestra / el
        // drawer termina de entrar). Sin ResizeObserver o sin window (jsdom /
        // SSR) renderizamos directo: no hay layout real a la cual esperar.
        renderWhenSized() {
            if (typeof window === "undefined" || typeof ResizeObserver === "undefined") {
                this.canRender = true;
                return;
            }
            const host = this.$refs.host;
            if (this.hasSize(host)) {
                this.canRender = true;
                return;
            }
            this._sizeObserver = new ResizeObserver(() => {
                if (this.hasSize(this.$refs.host)) {
                    this.canRender = true;
                    this.stopSizeObserver();
                }
            });
            this._sizeObserver.observe(host);
        },
        hasSize(el) {
            return !!el && el.clientWidth > 0 && el.clientHeight > 0;
        },
        stopSizeObserver() {
            if (this._sizeObserver) {
                this._sizeObserver.disconnect();
                this._sizeObserver = null;
            }
        },
        onChartClick(params) { this.$emit("chart-click", params); },
        onChartMouseover(params) { this.$emit("chart-mouseover", params); },
        onChartMouseout(params) { this.$emit("chart-mouseout", params); },
        // ECharts emite `finished` después de cada animación de render. Lo
        // exponemos como `chart-rendered` para callers que necesitan saber
        // cuando el chart es visualmente estable (p.ej. dispatch de analytics
        // o capturas post-render).
        onChartFinished() { this.$emit("chart-rendered"); },
    },
};
</script>

<style scoped lang="scss">
// El host envuelve al chart y llena el contenedor del consumer (que es quien
// define el tamaño). El chart llena el host. El host existe para poder medir
// el tamaño antes de montar el chart (ver renderWhenSized).
.echart_host,
.echart {
    width: 100%;
    height: 100%;
}
</style>
